import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import type { FunctionReturnType } from 'convex/server'
import {
  IconCircleCheckOutline18,
  IconCopyOutline18,
  IconLinkSlashOutline18,
  IconMobile2Outline18,
  IconQrcodeOutline18,
} from 'nucleo-ui-outline-18'
import { QRCodeSVG } from 'qrcode.react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '~/components/ui/alert-dialog'
import { Button } from '~/components/ui/button'
import { Spinner } from '~/components/ui/spinner'
import { capture } from '~/lib/posthog'
import { titleFor } from '~/lib/route-title'
import { cn } from '~/lib/utils'
import { useWorkspace } from '~/lib/workspace-context'
import { api } from '~convex/_generated/api'

export const Route = createFileRoute('/_authenticated/$workspaceSlug/account/whatsapp')({
  component: WhatsAppSettings,
  head: () => ({ meta: [{ title: titleFor(msg`WhatsApp`) }] }),
})

type PairingStatus = FunctionReturnType<typeof api.waPairing.status>
type OpenCode = NonNullable<PairingStatus['code']>
type ActiveLink = NonNullable<PairingStatus['link']>

const SECOND_MS = 1000
const MINUTE_MS = 60 * SECOND_MS

/** `+55` followed by area code, 4-or-5-digit prefix, and the final four. */
const BR_PHONE = /^\+55(\d{2})(\d{4,5})(\d{4})$/

/**
 * Brazilian numbers as people read them — `+55 49 9808-4958`. Anything that
 * isn't a `+55` number is left exactly as stored rather than mangled by a
 * format it doesn't fit.
 */
const formatPhone = (phoneE164: string): string => {
  const match = BR_PHONE.exec(phoneE164)
  if (match === null) {
    return phoneE164
  }
  return `+55 ${match[1]} ${match[2]}-${match[3]}`
}

/** Milliseconds left on the open code, re-read once a second. */
const useTimeRemaining = (expiresAt: number): number => {
  const [remaining, setRemaining] = useState(() => expiresAt - Date.now())
  useEffect(() => {
    setRemaining(expiresAt - Date.now())
    const timer = setInterval(() => setRemaining(expiresAt - Date.now()), SECOND_MS)
    return () => clearInterval(timer)
  }, [expiresAt])
  return remaining
}

const formatRemaining = (ms: number): string => {
  const total = Math.max(0, Math.floor(ms / SECOND_MS))
  const minutes = Math.floor(total / 60)
  const seconds = total % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

/**
 * Where a person connects their own phone to the Assistant. Lives on the
 * account surface rather than workspace settings because the link follows the
 * person between workspaces — it just remembers which one it acts on.
 */
function WhatsAppSettings() {
  const workspace = useWorkspace()
  const { data } = useQuery(convexQuery(api.waPairing.status, { workspaceId: workspace._id }))

  // Pairing completes on the server — the webhook consumes the code — so the
  // only thing the client can see is this panel replacing itself. Capture the
  // transition rather than the state: `undefined` is the first observation of
  // this mount, so a page load on an already-linked account reads as a visit
  // rather than a pairing. Without that distinction one activation would
  // report as one per page view.
  const wasLinked = useRef<boolean | undefined>(undefined)
  useEffect(() => {
    if (data === undefined) {
      return
    }
    const previous = wasLinked.current
    wasLinked.current = data.link !== null
    if (previous === false && data.link !== null) {
      capture('whatsapp_paired')
    }
  }, [data])

  if (data === undefined) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col gap-6 pt-2">
      {data.link === null ? (
        <NotConnected code={data.code} />
      ) : (
        <Connected link={data.link} workspaceName={workspace.name} />
      )}
      <p className="text-muted-foreground text-xs">
        <Trans>
          O Assistente is still learning. For now, it safely keeps everything you send in your
          workspace. Replies and actions will arrive as it grows.
        </Trans>
      </p>
    </div>
  )
}

const NotConnected = ({ code }: { code: OpenCode | null }) => {
  const { t } = useLingui()
  const workspace = useWorkspace()
  const { mutateAsync: startLinking, isPending } = useMutation({
    mutationFn: useConvexMutation(api.waPairing.startLinking),
  })

  const generate = async () => {
    try {
      await startLinking({ workspaceId: workspace._id })
    } catch {
      toast.error(t`Couldn't create a connection code. Try again.`)
    }
  }

  return (
    <section className="overflow-hidden rounded-xl border border-border">
      <div className="flex flex-col gap-3 p-5">
        <div className="flex size-9 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
          <IconMobile2Outline18 />
        </div>
        <div className="flex flex-col gap-1">
          <h2 className="font-medium text-base">
            <Trans>Talk to Planno on WhatsApp</Trans>
          </h2>
          <p className="max-w-prose text-muted-foreground text-sm">
            <Trans>
              Send voice notes, photos, or forwarded messages. O Assistente files them in{' '}
              {workspace.name}.
            </Trans>
          </p>
          <p className="max-w-prose text-muted-foreground text-xs">
            <Trans>Personal includes a 30-day trial; Professional has no time limit.</Trans>
          </p>
        </div>
        {code === null ? (
          <div className="pt-1">
            <Button disabled={isPending} onClick={generate}>
              <IconQrcodeOutline18 />
              <Trans>Create connection code</Trans>
            </Button>
          </div>
        ) : null}
      </div>
      {code === null ? null : <PairingPanel code={code} onRegenerate={generate} />}
    </section>
  )
}

/** Replaced by the connected card when the status subscription updates. */
const PairingPanel = ({ code, onRegenerate }: { code: OpenCode; onRegenerate: () => void }) => {
  const { t } = useLingui()
  const remaining = useTimeRemaining(code.expiresAt)
  const expired = remaining <= 0

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code.code)
      toast.success(t`Code copied.`)
    } catch {
      toast.error(t`Couldn't copy the code.`)
    }
  }

  if (expired) {
    return (
      <div className="flex flex-col items-start gap-3 border-border border-t bg-muted/30 p-5">
        <p className="text-muted-foreground text-sm">
          <Trans>This code expired after 15 minutes.</Trans>
        </p>
        <Button onClick={onRegenerate} variant="outline">
          <Trans>Create a new code</Trans>
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-5 border-border border-t bg-muted/30 p-5 sm:flex-row sm:gap-6">
      {/* Always light: a QR inverted for dark mode is a QR most scanners refuse. */}
      <div className="w-fit shrink-0 self-center rounded-lg bg-white p-3 shadow-sm sm:self-start">
        <QRCodeSVG bgColor="#ffffff" fgColor="#0a0a0a" level="M" size={148} value={code.deepLink} />
      </div>
      <div className="flex min-w-0 flex-col gap-4">
        <ol className="flex list-none flex-col gap-1.5 text-sm">
          <Step index={1}>
            <Trans>Open the camera on the phone you use for work.</Trans>
          </Step>
          <Step index={2}>
            <Trans>Scan the code to open WhatsApp.</Trans>
          </Step>
          <Step index={3}>
            <Trans>Send the message. This page connects automatically.</Trans>
          </Step>
        </ol>
        <div className="flex flex-wrap items-center gap-2">
          <button
            className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-2.5 py-1.5 font-medium font-mono text-sm tracking-[0.2em] transition-colors hover:bg-accent"
            onClick={copyCode}
            type="button"
          >
            {code.code}
            <IconCopyOutline18 className="size-3.5 text-muted-foreground" />
          </button>
          <span
            className={cn(
              'text-muted-foreground text-xs tabular-nums',
              remaining < MINUTE_MS && 'text-amber-600 dark:text-amber-400'
            )}
          >
            <Trans>Expires in {formatRemaining(remaining)}</Trans>
          </span>
        </div>
        <a
          className="text-muted-foreground text-xs underline underline-offset-4 hover:text-foreground sm:hidden"
          href={code.deepLink}
          rel="noreferrer"
          target="_blank"
        >
          <Trans>Already on your phone? Open WhatsApp</Trans>
        </a>
      </div>
    </div>
  )
}

const Step = ({ index, children }: { index: number; children: React.ReactNode }) => (
  <li className="flex gap-2.5">
    <span className="mt-px flex size-5 shrink-0 items-center justify-center rounded-full bg-background font-medium text-muted-foreground text-xs tabular-nums ring-1 ring-border">
      {index}
    </span>
    <span className="text-muted-foreground">{children}</span>
  </li>
)

/** The connected number, which workspace it acts on, and how to undo both. */
const Connected = ({ link, workspaceName }: { link: ActiveLink; workspaceName: string }) => {
  const { i18n, t } = useLingui()
  const workspace = useWorkspace()
  // Avoid a hook-shaped local binding.
  const { mutateAsync: moveLinkHere, isPending: switching } = useMutation({
    mutationFn: useConvexMutation(api.waPairing.useThisWorkspace),
  })
  const { mutateAsync: unlink, isPending: unlinking } = useMutation({
    mutationFn: useConvexMutation(api.waPairing.unlink),
  })
  const isBusy = switching || unlinking

  const moveHere = async () => {
    try {
      await moveLinkHere({ workspaceId: workspace._id })
      toast.success(t`O Assistente now works on ${workspaceName}.`)
    } catch {
      toast.error(t`Couldn't move the connection.`)
    }
  }

  const disconnect = async () => {
    try {
      await unlink({ workspaceId: workspace._id })
      toast.success(t`WhatsApp disconnected.`)
    } catch {
      toast.error(t`Couldn't disconnect. Try again.`)
    }
  }

  return (
    <section className="overflow-hidden rounded-xl border border-border">
      <div className="flex flex-col gap-4 p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
              <IconCircleCheckOutline18 />
            </div>
            <div className="flex min-w-0 flex-col">
              <span className="truncate font-medium text-sm tabular-nums">
                {formatPhone(link.phoneE164)}
              </span>
              <span className="text-muted-foreground text-xs">
                <Trans>Connected to O Assistente</Trans>
              </span>
            </div>
          </div>
          {/* The imperative confirm overlay does not mount on this surface. */}
          <AlertDialog>
            <AlertDialogTrigger
              render={
                <Button disabled={isBusy} size="sm" variant="ghost">
                  <Trans>Disconnect</Trans>
                </Button>
              }
            />
            <AlertDialogContent size="sm">
              <AlertDialogHeader>
                <AlertDialogMedia className="bg-destructive/10 text-destructive">
                  <IconLinkSlashOutline18 />
                </AlertDialogMedia>
                <AlertDialogTitle>
                  <Trans>Disconnect this number?</Trans>
                </AlertDialogTitle>
                <AlertDialogDescription>
                  <Trans>
                    Your message history stays. You can connect the same number again whenever you
                    want.
                  </Trans>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>
                  <Trans>Cancel</Trans>
                </AlertDialogCancel>
                <AlertDialogAction onClick={disconnect} variant="destructive">
                  <Trans>Disconnect</Trans>
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        <div className="flex flex-col gap-2 rounded-lg bg-muted/40 p-3.5">
          <span className="text-muted-foreground text-xs">
            <Trans>Messages from this number are filed in</Trans>
          </span>
          <span className="font-medium text-sm">{link.actsOnWorkspaceName ?? workspaceName}</span>
          {link.trialStatus === 'trial' && link.trialEndsAt !== null ? (
            <span className="text-muted-foreground text-xs">
              <Trans>
                Personal trial available until{' '}
                {i18n.date(new Date(link.trialEndsAt), { dateStyle: 'long' })}.
              </Trans>
            </span>
          ) : null}
          {link.trialStatus === 'expired' ? (
            <span className="text-amber-700 text-xs dark:text-amber-400">
              <Trans>
                Your 30-day Personal trial has ended. Upgrade this workspace to Professional to keep
                using O Assistente.
              </Trans>
            </span>
          ) : null}
          {link.actsOnThisWorkspace ? null : (
            <div className="pt-1">
              <Button disabled={isBusy} onClick={moveHere} size="sm" variant="outline">
                <Trans>Use {workspaceName} instead</Trans>
              </Button>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
